import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { CSS } from '@dnd-kit/utilities';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { findNodeLevel, formattedUpdateWeightBody, isLinkRow } from 'utils/linkLibrary';

import { QUICK_LINKS_QUERY_KEY, useUpdateLinkLibraryMutation } from 'hooks/query/useQuickLinksQuery';

import { LINK_LIBRARY_TYPES } from 'constants/linkLibrary';

import LinkTable from './LinkTable';
import SortableHeaderRow from './SortableHeaderRow';
import DndContextComponent from 'components/common/sortableDnD/DndContextComponent';

import { LinkLibraryTreeNode } from 'interface/linkLibraryTree';

interface Props {
  id: number | string;
  data: LinkLibraryTreeNode;
  nodeLevel?: number;
  activePopoverId: string | null;
  setActivePopoverId: React.Dispatch<React.SetStateAction<string | null>>;
}

const SortableTreeItem: React.FC<Props> = ({ id, data, nodeLevel = 0, activePopoverId, setActivePopoverId }) => {
  const queryClient = useQueryClient();
  const [listDraft, setListDraft] = useState(() => ({ sourceData: data, listData: data }));
  const [childrenDraft, setChildrenDraft] = useState(() => ({
    sourceChildren: data.children,
    listChildrenData: [...(data.children || [])],
  }));
  const listData =
    listDraft.sourceData === data
      ? listDraft.listData
      : {
          ...data,
          isExpanded: listDraft.listData.isExpanded ?? data.isExpanded,
        };
  const setListData = (value: React.SetStateAction<LinkLibraryTreeNode>) => {
    setListDraft((prev) => {
      const currentListData = prev.sourceData === data ? prev.listData : listData;
      const nextListData = typeof value === 'function' ? value(currentListData) : value;

      return {
        sourceData: data,
        listData: nextListData,
      };
    });
  };
  const listChildrenData =
    childrenDraft.sourceChildren === data.children ? childrenDraft.listChildrenData : [...(data.children || [])];
  const setListChildrenData = (value: React.SetStateAction<LinkLibraryTreeNode[]>) => {
    setChildrenDraft((prev) => {
      const currentListChildrenData =
        prev.sourceChildren === data.children ? prev.listChildrenData : [...(data.children || [])];
      const nextListChildrenData = typeof value === 'function' ? value(currentListChildrenData) : value;

      return {
        sourceChildren: data.children,
        listChildrenData: nextListChildrenData,
      };
    });
  };
  const treeData = queryClient.getQueryData<LinkLibraryTreeNode[]>(QUICK_LINKS_QUERY_KEY) || [];

  const { mutate } = useUpdateLinkLibraryMutation();

  const { transform, transition, attributes, setNodeRef, listeners, setActivatorNodeRef } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { SUBCATEGORY, LINK } = LINK_LIBRARY_TYPES;

  const sortableChildrenIds = listChildrenData
    .map((item) => item.id)
    .filter((itemId): itemId is number | string => itemId !== undefined && itemId !== null);

  const onDragEnd = (items: LinkLibraryTreeNode[]) => {
    const bodyPayload = formattedUpdateWeightBody(items);

    setListChildrenData(items);
    mutate(bodyPayload);
  };

  return (
    <div className="direct-row" ref={setNodeRef} style={style}>
      <SortableHeaderRow
        listData={listData}
        setListData={setListData}
        attributes={attributes}
        listeners={listeners}
        setActivatorNodeRef={setActivatorNodeRef}
        nodeLevel={nodeLevel}
        activePopoverId={activePopoverId}
        setActivePopoverId={setActivePopoverId}
      />
      {listChildrenData.length > 0 && (
        <div className={!listData.isExpanded ? 'rst__content' : 'rst__content--active'}>
          <DndContextComponent listData={listChildrenData} onDragEnd={onDragEnd}>
            <ul>
              <SortableContext items={sortableChildrenIds} strategy={verticalListSortingStrategy}>
                {listChildrenData.map((item) => {
                  if (item.type === SUBCATEGORY) {
                    return (
                      <li key={`${item.id}-${item.name}`}>
                        <SortableTreeItem
                          id={item.id}
                          data={item}
                          nodeLevel={findNodeLevel(treeData, { id: item.id, type: item.type })}
                          activePopoverId={activePopoverId}
                          setActivePopoverId={setActivePopoverId}
                        />
                      </li>
                    );
                  }

                  if (item.type === LINK) {
                    const linkRows = ((item.children || item.links || []) as LinkLibraryTreeNode[]).filter(isLinkRow);

                    return (
                      <li key={item.id ?? `${item.categoryId}-${item.type}`}>
                        <LinkTable
                          links={linkRows}
                          nodeLevel={findNodeLevel(treeData, { id: item.categoryId || item.id, type: item.type })}
                          activePopoverId={activePopoverId}
                          setActivePopoverId={setActivePopoverId}
                        />
                      </li>
                    );
                  }

                  return null;
                })}
              </SortableContext>
            </ul>
          </DndContextComponent>
        </div>
      )}
    </div>
  );
};

export default SortableTreeItem;
